import React from 'react';

import { useShallowEffect } from '@mantine/hooks';
import { RichTextEditor } from '@mantine/tiptap';
import { Link } from '@tiptap/extension-link';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { AnyExtension, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { normalizeToHtml } from 'utils/html';

interface ReadonlyWorkoutNotesProps {
  workoutNotes: string | null | undefined;
  mih?: number;
  bordered?: boolean;
  fontSize?: string | number;
}

const ReadonlyWorkoutNotesRTE = ({
  workoutNotes,
  mih = 200,
  bordered = true,
  fontSize,
}: ReadonlyWorkoutNotesProps): JSX.Element => {
  const editor = useEditor({
    extensions: [
      StarterKit as AnyExtension,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer nofollow',
          target: '_blank',
        },
      }),
    ],
    content: normalizeToHtml(workoutNotes),
    editable: false,
  });

  useShallowEffect(() => {
    editor?.commands.setContent(normalizeToHtml(workoutNotes));
  }, [workoutNotes]);

  return (
    <RichTextEditor
      editor={editor}
      styles={
        bordered
          ? undefined
          : theme => ({
              root: { border: 'none' },
              content: {
                backgroundColor: 'transparent',
                // Inline (borderless) mode replaces the old plain-text fields, which
                // rendered at `sm`. Match that so notes sit flush with the surrounding
                // preview text instead of the editor's default `md`.
                '& .ProseMirror': { padding: 0, fontSize: fontSize ?? theme.fontSizes.sm },
              },
            })
      }
    >
      <RichTextEditor.Content mih={mih} h='100%' data-cy='programRte' />
    </RichTextEditor>
  );
};

export default ReadonlyWorkoutNotesRTE;
